/**
 * **Platz tauschen** innerhalb einer Mehrfachauswahl — die Geste am
 * **Tauschpunkt** (siehe CONTEXT.md).
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: die Geometrie soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import { same } from './canvasAlignment'
import type { MovingRect } from './canvasAlignment'

/**
 * Die Höhe, auf die das GETRAGENE Element gehoben wird — über alle anderen
 * Nodes (deren `zIndex` ist ihr Index nach Alter, siehe `noteToFlowNode`).
 * React Flow rechnet für Ausgewählte noch +1000 darauf (`calculateZ` in
 * @xyflow/system).
 */
export const SWAP_LIFTED_Z = 10000

/**
 * Die Höhe des GEGRIFFENEN Tauschpunkts: über dem getragenen Element samt
 * React Flows Zuschlag. Läge er darunter, verschwände er beim Anfassen — und
 * das `pointerup` träfe den Zettel statt den Ring, womit `click` und
 * `dblclick` auf dem gemeinsamen Vorfahren landeten und der Doppelklick auf
 * den Ring nie ankäme (so in der App gesehen).
 */
export const SWAP_RING_LIFTED_Z = SWAP_LIFTED_Z + 1000 + 1

/** Ein ausgewähltes Canvas-Element, wie es durch die Geste läuft. */
export interface SwapEntry {
  id: string
  rect: MovingRect
  /**
   * Die Farbe seines **Tauschpunkts** — der Ring trägt die Farbe des Zettels,
   * auf dem er sitzt (`getCanvasNoteRingColor`). Ohne Angabe der Ton der
   * Vorgabefarbe; das ist ein Zugeständnis an Tests und Geschichten, in der
   * App setzt `CanvasFlow` ihn immer.
   */
  accent?: string
  /**
   * Der **Bereich**, zu dem es gehört — `null`/fehlend heißt: keiner. An dieser
   * Grenze endet eine **Reihe** (`rowAfter` in `canvasSpawn.ts`): Zettel rücken
   * nur mit Zetteln derselben Welt. Wie beim Akzent ein Zugeständnis an Tests
   * und Geschichten; in der App setzt `CanvasFlow` ihn immer.
   */
  areaId?: string | null
  /**
   * Ob ein **Klebestreifen** darauf liegt. Ein geklebter Zettel ist kein
   * **Tauschpartner** — er rührt sich nicht (siehe `findSwapTarget`).
   */
  taped?: boolean
}

/** Ein Punkt in Canvas-Koordinaten. */
export interface SwapPoint {
  x: number
  y: number
}

/** Die Werte eines Rechtecks auf EINER Achse. */
interface AxisView {
  /** Führende Kante — links bzw. oben. */
  lead: number
  /** Folgende Kante — rechts bzw. unten. */
  trail: number
  center: number
  size: number
}

const axisOf = (rect: MovingRect, axis: 'x' | 'y'): AxisView =>
  axis === 'x'
    ? { lead: rect.x, trail: rect.x + rect.width, center: rect.x + rect.width / 2, size: rect.width }
    : { lead: rect.y, trail: rect.y + rect.height, center: rect.y + rect.height / 2, size: rect.height }

/**
 * Ob zwei Elemente auf einer Achse **fluchten** — gleiche führende Kante,
 * gleiche folgende Kante oder gleiche Mitte.
 *
 * Dieselbe Frage, die auch die **Hilfslinien** stellen, und deshalb mit
 * derselben Antwort: `same` aus `canvasAlignment`, halber Pixel Toleranz.
 */
function alignedOn(a: MovingRect, b: MovingRect, axis: 'x' | 'y'): boolean {
  const A = axisOf(a, axis)
  const B = axisOf(b, axis)
  return same(A.lead, B.lead) || same(A.trail, B.trail) || same(A.center, B.center)
}

/**
 * Das ausgewählte Element unter dem Zeiger — der Partner, mit dem getauscht
 * wird.
 *
 * **Nur was unter dem Zeiger liegt**, keine Nähe-Schwelle und kein
 * „nächstgelegenes": Beides bräuchte eine erfundene Zahl und ließe einen
 * danebenzielen. „Zeig auf das, mit dem du tauschen willst" versteht man ohne
 * Erklärung — und liegt dort nichts, passiert eben nichts.
 *
 * **Gefragt wird gegen die eingefrorene Ausgangslage**, nie gegen die laufende
 * Vorschau (siehe `useSwapDrag`): Man zielt auf einen PLATZ, nicht auf ein
 * wanderndes Element. Sonst zerstörte sich die Vorschau selbst — der Partner
 * rutscht weg, hier findet sich nichts mehr, die Vorschau nimmt sich zurück,
 * der Partner steht wieder da, und das mehrmals pro Sekunde.
 *
 * Liegen mehrere übereinander, gewinnt das **oberste**. Die Einträge kommen in
 * der Reihenfolge der Nodes, und die ist nach Alter sortiert (`byAge` in
 * `CanvasFlow`, `zIndex` = Index) — der letzte Treffer liegt also oben, genau
 * der, den man anklicken würde.
 *
 * **Ein geklebter Zettel ist kein Partner.** Er rührt sich nicht, also gibt es
 * nichts zu tauschen; der Zeiger geht über ihn hinweg zu dem, was darunter
 * liegt, oder findet nichts. Siehe „Klebestreifen" in CONTEXT.md.
 *
 * @param dragged die Kennung des gezogenen Elements; es kann nicht sein
 *                eigener Partner sein
 */
export function findSwapTarget(
  entries: SwapEntry[],
  dragged: string,
  pointer: SwapPoint,
): SwapEntry | null {
  let found: SwapEntry | null = null

  for (const entry of entries) {
    if (entry.id === dragged) continue
    if (entry.taped) continue
    const { x, y, width, height } = entry.rect
    if (pointer.x < x || pointer.x > x + width) continue
    if (pointer.y < y || pointer.y > y + height) continue
    found = entry
  }

  return found
}

/**
 * Die Lagen zweier Elemente, getauscht.
 *
 * > **Fluchten die beiden auf einer Achse, bleibt diese Achse unangetastet.
 * > Sonst tauschen dort die Mitten.**
 *
 * Beide Achsen werden getrennt entschieden. Daraus fallen alle drei Lagen von
 * selbst heraus:
 *
 * | Lage | was passiert |
 * |---|---|
 * | nebeneinander, Höhe fluchtet | y bleibt, auf x tauschen die Mitten |
 * | übereinander, Spalte fluchtet | x bleibt, auf y tauschen die Mitten |
 * | diagonal | beide Achsen tauschen die Mitten |
 *
 * **Warum nicht schlicht `x` und `y` tauschen:** Bei gleich großen Elementen
 * wäre das dasselbe — bei ungleich großen zerfällt es. A ist 100 breit bei
 * x=0, B ist 300 breit bei x=200, die Oberkanten fluchten:
 *
 * ```
 * Ecke tauschen    A → 200…300, B → 0…300     sie liegen übereinander
 * Mitten tauschen  A → 300…400, B → −100…200  Lücke bleibt, Oberkanten nicht
 * je Achse         A → 300…400, B → −100…200  und y bleibt 0 — beides hält
 * ```
 *
 * Die Ecke zu tauschen bewahrt das Fluchten und erzeugt Überlappung; die
 * Mitten zu tauschen bewahrt die Lücke und zerstört das Fluchten. Getrennt
 * entschieden hält jede Achse das, worauf es dort ankommt.
 *
 * **Was die Regel nicht kann:** Fluchten zwei sehr unterschiedlich große
 * Elemente auf KEINER Achse, kann das größere danach einen dritten
 * überlappen. Dagegen hülfe nur echtes Umordnen mit Nachrücken.
 *
 * **Die Maße bleiben unberührt** — es tauschen die Plätze, nicht die Größen.
 */
export function swapPositions(a: MovingRect, b: MovingRect): { a: SwapPoint; b: SwapPoint } {
  const next = (rect: MovingRect, other: MovingRect, axis: 'x' | 'y'): number => {
    const mine = axisOf(rect, axis)
    // Fluchtende Achse: nichts zu tun. Genau hier bleibt „waagerecht tauschen
    // lässt y in Ruhe" erhalten, ohne dass es eine eigene Regel bräuchte.
    if (alignedOn(a, b, axis)) return mine.lead
    // Sonst: meine Mitte auf seine, zurückgerechnet auf die führende Kante.
    return axisOf(other, axis).center - mine.size / 2
  }

  return {
    a: { x: next(a, b, 'x'), y: next(a, b, 'y') },
    b: { x: next(b, a, 'x'), y: next(b, a, 'y') },
  }
}
